import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Check, RotateCw, Crop, Send, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { EditeurCoins } from './EditeurCoins';
import { PleinEcran, Alertes, Traitement, tailleKo } from './PleinEcran';
import { preparerPage, base64EnFichier, COINS_DEFAUT } from './scanUtils';

const clair = 'border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white';
const vert = 'bg-[#8DC63F] text-[#0F172A] hover:bg-[#7AB534]';

// Rognage automatique : photo → scan recadré tout de suite ; « Ajuster les coins » seulement si besoin
export const ScanDocumentDialog = ({ doc, mode, multi, premiere, onClose, onFichier }) => {
  const carte = mode === 'carte';
  const [pages, setPages] = useState([premiere]);
  const [index, setIndex] = useState(0);
  const [etape, setEtape] = useState(carte || multi ? 'suite' : 'apercu');
  const [resultat, setResultat] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const recommencer = useRef(false);
  const nomPage = (i) => (carte ? (i === 0 ? 'recto' : 'verso') : `page ${i + 1}`);

  const apercu = async (liste) => {
    setEtape('apercu'); setResultat(null); setBusy(true);
    const fd = new FormData();
    fd.append('mode', mode);
    fd.append('params', JSON.stringify(liste.map((p) => (p.coins ? { coins: p.coins, rotation: p.rotation } : { auto: true, rotation: p.rotation }))));
    liste.forEach((p, i) => fd.append('pages', p.blob, `page${i + 1}.jpg`));
    try {
      const { data } = await axios.post('/scan/apercu', fd, { timeout: 90000 });
      setPages(liste.map((p, i) => ({ ...p, coins: data.coins?.[i] || p.coins })));
      setResultat(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transformation impossible, reprenez la photo');
      setPages(liste); setIndex(0); setEtape('coins');
    } finally { setBusy(false); }
  };
  const lance = useRef(false);
  useEffect(() => {
    if (!carte && !multi && !lance.current) { lance.current = true; apercu([premiere]); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nouvellePhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const p = await preparerPage(f);
      const liste = recommencer.current ? [p] : [...pages, p];
      setPages(liste); setIndex(liste.length - 1); setResultat(null);
      if ((carte && liste.length === 1) || (multi && liste.length < 3)) setEtape('suite');
      else apercu(liste);
    } catch { toast.error('Photo illisible, réessayez'); }
  };
  const photographier = (depuisZero) => { recommencer.current = depuisZero; input.current?.click(); };
  const validerCoins = () => (index < pages.length - 1 ? setIndex(index + 1) : apercu(pages));
  const pivoter = (i) => apercu(pages.map((p, j) => (j === i ? { ...p, rotation: (p.rotation + 90) % 360 } : p)));
  const envoyer = async () => {
    setBusy(true);
    try { await onFichier(base64EnFichier(resultat.fichier_base64, `${doc.id}.${resultat.extension}`, resultat.content_type)); onClose(); } finally { setBusy(false); }
  };
  const changerCoins = (coins) => setPages((l) => l.map((p, i) => (i === index ? { ...p, coins } : p)));

  const vues = {
    coins: {
      sousTitre: `Glissez les 4 ronds verts sur les coins ${carte ? 'de la carte' : 'de la feuille'} (${nomPage(index)})`,
      contenu: <EditeurCoins url={pages[index].url} coins={pages[index].coins || COINS_DEFAUT} onChange={changerCoins} />,
      actions: <Button className={vert} onClick={validerCoins} data-testid="scan-valider-coins"><Check className="h-4 w-4 mr-2" /> {index < pages.length - 1 ? `Valider puis ${nomPage(index + 1)}` : 'Valider le cadrage'}</Button>,
    },
    suite: {
      sousTitre: carte ? 'Le recto et le verso seront réunis sur une seule page A4' : 'Ajoutez une autre page si votre document en a plusieurs',
      contenu: (
        <div className="flex flex-col items-center gap-3">
          <img src={pages[pages.length - 1].url} alt="Dernière photo" className="max-h-[40vh] rounded-md opacity-90" data-testid="scan-derniere-photo" />
          <p className="max-w-sm text-center text-white/80">{carte ? 'Recto photographié. Retournez la carte et photographiez le verso.' : `${pages.length} page(s) photographiée(s).`}</p>
        </div>
      ),
      actions: carte ? <>
        <Button variant="outline" className={clair} onClick={() => apercu(pages)} data-testid="scan-sans-verso">Pas de verso (passeport…)</Button>
        <Button className={vert} onClick={() => photographier(false)} data-testid="scan-photographier-verso"><Camera className="h-4 w-4 mr-2" /> Photographier le verso</Button>
      </> : <>
        <Button variant="outline" className={clair} onClick={() => photographier(false)} data-testid="scan-ajouter-page"><Plus className="h-4 w-4 mr-2" /> Ajouter une page</Button>
        <Button className={vert} onClick={() => apercu(pages)} data-testid="scan-terminer"><Check className="h-4 w-4 mr-2" /> Terminer</Button>
      </>,
    },
    apercu: {
      sousTitre: resultat ? `PDF A4 portrait · ${resultat.nb_pages} page${resultat.nb_pages > 1 ? 's' : ''} · ${tailleKo(resultat.taille)}` : 'Recadrage automatique, ombres retirées, mise en page A4',
      contenu: busy && !resultat ? <Traitement texte="Recadrage automatique…" /> : resultat && (
        <div className="flex w-full max-w-md flex-col items-center gap-3">
          <img src={`data:image/jpeg;base64,${resultat.apercu_base64}`} alt="Aperçu du scan" className="max-h-[50vh] rounded-md bg-white shadow-2xl" data-testid="scan-apercu-image" />
          <Alertes alertes={resultat.alertes} />
        </div>
      ),
      actions: resultat && <>
        <Button variant="outline" className={clair} onClick={() => photographier(true)} disabled={busy} data-testid="scan-reprendre"><Camera className="h-4 w-4 mr-2" /> Reprendre</Button>
        <Button variant="outline" className={clair} onClick={() => { setIndex(0); setEtape('coins'); }} disabled={busy} data-testid="scan-ajuster-coins"><Crop className="h-4 w-4 mr-2" /> Ajuster les coins</Button>
        {pages.map((p, i) => <Button key={i} variant="outline" className={clair} onClick={() => pivoter(i)} disabled={busy} data-testid={`scan-pivoter-${i}`}><RotateCw className="h-4 w-4 mr-2" /> Pivoter{pages.length > 1 ? ` ${nomPage(i)}` : ''}</Button>)}
        <Button className={vert} onClick={envoyer} disabled={busy} data-testid="scan-envoyer"><Send className="h-4 w-4 mr-2" /> Ajouter au dossier</Button>
      </>,
    },
  };
  const v = vues[etape];
  return (
    <PleinEcran titre={doc.nom} sousTitre={v.sousTitre} onClose={onClose} actions={v.actions} testId="scan-document">
      {v.contenu}
      <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" onChange={nouvellePhoto} data-testid="scan-input" />
    </PleinEcran>
  );
};
